<template>
    <div class="py-6">
        <!-- No Sure URL configured or no user / invalid iframe src -->
        <template v-if="!sureInsuranceUrl || !iframeSrc">
            <div class="rounded-lg border border-gray-200 bg-gray-50 p-6">
                <p class="text-gray-500 text-sm">Renter's insurance is not available at the moment. Please try again later or contact support.</p>
            </div>
        </template>

        <!-- Embedded Sure iframe -->
        <template v-else>
            <div class="rounded-lg border border-gray-200 bg-white overflow-hidden">
                <iframe
                    :src="iframeSrc"
                    class="block w-full border-0"
                    style="min-height: 80vh; height: 800px;"
                    title="Renter's Insurance"
                />
            </div>
        </template>
    </div>
</template>

<script setup>
import { computed, onMounted, onUnmounted } from 'vue'
import { usePage } from '@inertiajs/vue3'
import axios from 'axios'

const props = defineProps({
    property: { type: Object, default: null },
    tabData: { type: Object, default: () => ({}) },
})

const page = usePage()
const user = computed(() => page.props.user ?? null)
const sureInsuranceUrl = computed(() => props.tabData?.rentersInsurance?.sure_insurance_url ?? null)

const iframeSrc = computed(() => {
    const baseUrl = sureInsuranceUrl.value
    if (!baseUrl || !user.value) return ''

    const nameParts = (user.value.name || '').trim().split(/\s+/)
    const firstName = nameParts[0] ?? ''
    const lastName = nameParts.slice(1).join(' ') ?? ''

    const params = {
        embed: 'true',
        street_address: props.property?.address_1,
        city: props.property?.city,
        region: props.property?.state,
        postal: props.property?.zip_code,
        first_name: firstName,
        last_name: lastName,
        email: user.value.email ?? '',
    }

    const queryString = Object.entries(params)
        .filter(([, v]) => v != null && v !== '')
        .map(([key, val]) => `${encodeURIComponent(key)}=${encodeURIComponent(val)}`)
        .join('&')

    return `${baseUrl}?${queryString}`
})

async function handleMessage(event) {
    try {
        // Only accept messages from the Sure iframe origin
        const baseUrl = sureInsuranceUrl.value
        if (!baseUrl) return
        let allowedOrigin = ''
        try {
            allowedOrigin = new URL(baseUrl).origin
        } catch {
            return
        }
        if (event.origin !== allowedOrigin) return
        if (event.data?.actionType === 'PURCHASE_RENTERS_SUCCESS') {
            await axios.post(route('renters-insurance.store'), {
                policy_data: event.data,
            })
        }
    } catch (_) {
        // Silently ignore store errors; iframe may still show success
    }
}

onMounted(() => {
    window.addEventListener('message', handleMessage)
})

onUnmounted(() => {
    window.removeEventListener('message', handleMessage)
})
</script>
